import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';

const CURRENT_TERMS_VERSION = '2025-11-02';

/**
 * Custom hook to check and manage Terms & Conditions acceptance
 * @param {Object|null} user - Firebase auth user object
 * @returns {Object} - { needsAcceptance, isChecking, acceptTerms }
 */
export function useTermsAcceptance(user) {
  const [needsAcceptance, setNeedsAcceptance] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function checkTermsAcceptance() {
      if (!user || user.isAnonymous) {
        // Not logged in or anonymous guest - no terms required
        if (isMounted) {
          setNeedsAcceptance(false);
          setIsChecking(false);
        }
        return;
      }

      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (!isMounted) return;

        if (!userDoc.exists()) {
          // New user - needs to accept terms
          setNeedsAcceptance(true);
        } else {
          const userData = userDoc.data();
          const acceptedVersion = userData?.acceptedTermsVersion;

          if (!acceptedVersion || acceptedVersion !== CURRENT_TERMS_VERSION) {
            // No acceptance record or outdated version - show modal
            setNeedsAcceptance(true);
          } else {
            // Already accepted current version
            setNeedsAcceptance(false);
          }
        }
      } catch (error) {
        console.error('Error checking terms acceptance:', error);
        // On error, assume they need to accept (safer default)
        if (isMounted) {
          setNeedsAcceptance(true);
        }
      } finally {
        if (isMounted) {
          setIsChecking(false);
        }
      }
    }

    checkTermsAcceptance();

    return () => {
      isMounted = false;
    };
  }, [user]);

  /**
   * Store terms acceptance in Firestore
   * @param {string} version - Terms version accepted
   */
  const acceptTerms = async (version) => {
    if (!user) {
      throw new Error('User must be logged in to accept terms');
    }

    try {
      const userDocRef = doc(db, 'users', user.uid);
      
      await setDoc(
        userDocRef,
        {
          acceptedTermsVersion: version,
          acceptedAt: serverTimestamp(),
          email: user.email,
          displayName: user.displayName || null,
          photoURL: user.photoURL || null,
        },
        { merge: true }
      );

      setNeedsAcceptance(false);
      return true;
    } catch (error) {
      console.error('Error accepting terms:', error);
      throw error;
    }
  };

  return {
    needsAcceptance,
    isChecking,
    acceptTerms,
    currentVersion: CURRENT_TERMS_VERSION,
  };
}

export { CURRENT_TERMS_VERSION };
